import { useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ConsentDocuments } from '@/api/entities';
import MarkdownDocument from '@/components/consent/MarkdownDocument';
import { Plus, Eye, Archive, Loader2, Pencil, ExternalLink } from 'lucide-react';

const DOCUMENT_TYPES = [
  { value: 'agreement', label: 'Согласие на обработку персональных данных' },
  { value: 'privacy_policy', label: 'Политика обработки персональных данных' },
  { value: 'oferta', label: 'Оферта' },
  { value: 'eula', label: 'Пользовательское соглашение' },
  { value: 'marketing', label: 'Маркетинговые коммуникации' }
];

export default function ConsentDocumentsSettings() {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [viewerDoc, setViewerDoc] = useState(null);
  const [editingDoc, setEditingDoc] = useState(null);
  const [showPublish, setShowPublish] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [savingEdit, setSavingEdit] = useState(false);
  const [form, setForm] = useState({ type: 'agreement', name: '', version: '', content: '' });
  const [editForm, setEditForm] = useState({ name: '', version: '', content: '' });

  const reload = async () => {
    setLoading(true);
    setError(null);
    try {
      const list = await ConsentDocuments.list({ includeArchived: true });
      setDocs(Array.isArray(list) ? list : []);
    } catch (e) {
      setError(e?.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); }, []);

  // Группируем по type+name; внутри — отсортировано по effective_from DESC
  const grouped = useMemo(() => {
    const map = new Map();
    for (const d of docs) {
      const key = `${d.type}::${d.name}`;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(d);
    }
    for (const arr of map.values()) {
      arr.sort((a, b) => new Date(b.effective_from) - new Date(a.effective_from));
    }
    return Array.from(map.entries());
  }, [docs]);

  const submitPublish = async () => {
    if (!form.type || !form.name.trim() || !form.content.trim()) return;
    setPublishing(true);
    try {
      await ConsentDocuments.publish({
        type: form.type.trim(),
        name: form.name.trim(),
        version: form.version.trim() || null,
        content: form.content
      });
      setShowPublish(false);
      setForm({ type: 'agreement', name: '', version: '', content: '' });
      await reload();
    } catch (e) {
      alert(`Не удалось опубликовать: ${e.message}`);
    } finally {
      setPublishing(false);
    }
  };

  const retire = async (type, name) => {
    if (!window.confirm(`Вывести "${name}" (${type}) из обращения? Старые согласия останутся валидными.`)) return;
    try {
      await ConsentDocuments.retire({ type, name });
      await reload();
    } catch (e) {
      alert(`Не удалось: ${e.message}`);
    }
  };

  const startEdit = (doc) => {
    setEditingDoc(doc);
    setEditForm({
      name: doc.name || '',
      version: doc.version || '',
      content: doc.content || ''
    });
  };

  const submitEdit = async () => {
    if (!editingDoc || !editForm.name.trim() || !editForm.content.trim()) return;
    setSavingEdit(true);
    try {
      const updated = await ConsentDocuments.update(editingDoc.id, {
        name: editForm.name.trim(),
        version: editForm.version.trim() || null,
        content: editForm.content
      });
      setEditingDoc(null);
      setViewerDoc(current => current?.id === updated.id ? updated : current);
      await reload();
    } catch (e) {
      alert(`Не удалось сохранить: ${e.message}`);
    } finally {
      setSavingEdit(false);
    }
  };

  const startNewVersion = (type, name) => {
    const last = docs.find(d => d.type === type && d.name === name && d.is_current)
                || docs.find(d => d.type === type && d.name === name);
    setForm({
      type,
      name,
      version: '',
      content: last?.content || ''
    });
    setShowPublish(true);
  };

  return (
    <Card className="elegant-card">
      <CardHeader className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <CardTitle>Документы и согласия</CardTitle>
        <Button
          className="w-full sm:w-auto"
          onClick={() => { setForm({ type: 'agreement', name: '', version: '', content: '' }); setShowPublish(true); }}
        >
          <Plus className="h-4 w-4 mr-2" /> Опубликовать новый документ
        </Button>
      </CardHeader>
      <CardContent>
        {loading && <div className="flex items-center text-sm text-gray-500"><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Загрузка...</div>}
        {error && <div className="text-red-600 text-sm">{error}</div>}

        {!loading && !grouped.length && (
          <p className="text-sm text-gray-500">
            Документов пока нет. Опубликуйте оферту и политику обработки ПД, чтобы они появились в формах оформления заказа и подписки на waitlist.
          </p>
        )}

        <div className="space-y-6">
          {grouped.map(([key, versions]) => {
            const [type, name] = key.split('::');
            const current = versions.find(v => v.is_current);
            return (
              <div key={key} className="border rounded-lg p-3 sm:p-4">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-3">
                  <div className="min-w-0">
                    <div className="font-medium break-words">{name}</div>
                    <div className="text-xs text-gray-500">тип: <code>{type}</code></div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {current && (
                      <Button size="sm" variant="outline" asChild>
                        <a href={`/documents/${encodeURIComponent(type)}`} target="_blank" rel="noreferrer">
                          <ExternalLink className="h-3 w-3 mr-1" /> Публичная
                        </a>
                      </Button>
                    )}
                    <Button size="sm" variant="outline" onClick={() => startNewVersion(type, name)}>
                      <Plus className="h-3 w-3 mr-1" /> Новая версия
                    </Button>
                    {current && (
                      <Button size="sm" variant="outline" onClick={() => retire(type, name)}>
                        <Archive className="h-3 w-3 mr-1" /> Вывести
                      </Button>
                    )}
                  </div>
                </div>

                <div className="space-y-1">
                  {versions.map(v => (
                    <div key={v.id} className="flex flex-wrap items-center justify-between gap-2 text-sm border-b last:border-b-0 py-2">
                      <div className="flex flex-wrap items-center gap-2 min-w-0">
                        {v.is_current
                          ? <Badge className="bg-emerald-600">текущая</Badge>
                          : <Badge variant="secondary">архив</Badge>}
                        <span className="font-mono text-xs">{v.version || '—'}</span>
                        <span className="text-gray-600 text-xs sm:text-sm break-words">
                          с {new Date(v.effective_from).toLocaleString()}
                          {v.effective_to ? <> по {new Date(v.effective_to).toLocaleString()}</> : null}
                        </span>
                      </div>
                      <div className="flex flex-wrap items-center gap-1">
                        <Button size="sm" variant="ghost" onClick={() => setViewerDoc(v)}>
                          <Eye className="h-3 w-3 mr-1" /> Текст
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => startEdit(v)}>
                          <Pencil className="h-3 w-3 mr-1" /> Править
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        {/* Просмотр текста версии */}
        <Dialog open={!!viewerDoc} onOpenChange={(open) => !open && setViewerDoc(null)}>
          <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {viewerDoc?.name}
                {viewerDoc?.version ? ` (v${viewerDoc.version})` : ''}
              </DialogTitle>
            </DialogHeader>
            <MarkdownDocument content={viewerDoc?.content} />
          </DialogContent>
        </Dialog>

        {/* Редактирование сохранённой версии */}
        <Dialog open={!!editingDoc} onOpenChange={(open) => !open && setEditingDoc(null)}>
          <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                Редактировать документ
                {editingDoc?.is_current ? ' (текущая версия)' : ' (архивная версия)'}
              </DialogTitle>
            </DialogHeader>
            <div className="grid gap-4 lg:grid-cols-2">
              <div className="space-y-3">
                <div>
                  <Label>Тип</Label>
                  <Input value={editingDoc?.type || ''} disabled />
                </div>
                <div>
                  <Label>Название</Label>
                  <Input
                    value={editForm.name}
                    onChange={(e) => setEditForm(f => ({ ...f, name: e.target.value }))}
                  />
                </div>
                <div>
                  <Label>Версия</Label>
                  <Input
                    value={editForm.version}
                    onChange={(e) => setEditForm(f => ({ ...f, version: e.target.value }))}
                    placeholder="1.0 или 2026-05"
                  />
                </div>
                <div>
                  <Label>Текст (Markdown)</Label>
                  <Textarea
                    value={editForm.content}
                    onChange={(e) => setEditForm(f => ({ ...f, content: e.target.value }))}
                    rows={18}
                    className="font-mono text-sm"
                  />
                </div>
              </div>
              <div>
                <Label>Предпросмотр</Label>
                <div className="mt-2 max-h-[640px] overflow-y-auto rounded-md border bg-white p-4">
                  <MarkdownDocument content={editForm.content} />
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setEditingDoc(null)} disabled={savingEdit}>Отмена</Button>
              <Button onClick={submitEdit} disabled={savingEdit || !editForm.name.trim() || !editForm.content.trim()}>
                {savingEdit && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Сохранить
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Публикация новой версии */}
        <Dialog open={showPublish} onOpenChange={(open) => !open && setShowPublish(false)}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Опубликовать версию документа</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <Label>Тип документа</Label>
                <Select
                  value={form.type}
                  onValueChange={(value) => setForm(f => ({ ...f, type: value }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Выберите тип документа" />
                  </SelectTrigger>
                  <SelectContent>
                    {DOCUMENT_TYPES.map(type => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label} ({type.value})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Название</Label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))}
                  placeholder="Политика обработки персональных данных"
                />
              </div>
              <div>
                <Label>Версия (опционально)</Label>
                <Input
                  value={form.version}
                  onChange={(e) => setForm(f => ({ ...f, version: e.target.value }))}
                  placeholder="1.0 или 2026-05"
                />
              </div>
              <div>
                <Label>Текст (Markdown)</Label>
                <Textarea
                  value={form.content}
                  onChange={(e) => setForm(f => ({ ...f, content: e.target.value }))}
                  rows={14}
                  className="font-mono text-sm"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Прежняя текущая версия для этого type+name автоматически архивируется.
                </p>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setShowPublish(false)} disabled={publishing}>Отмена</Button>
                <Button onClick={submitPublish} disabled={publishing || !form.type || !form.name.trim() || !form.content.trim()}>
                  {publishing && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Опубликовать
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
}
